import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { z } from 'zod'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { parseOutlookLinkRefusal } from './link-code-refusal'

const LinkCodePreviewSchema = z.object({
  microsoft_sign_in_name: z.string().nullable(),
  expires_at: z.string().datetime({ offset: true }),
})

interface LinkCodePreview {
  /** Sign-in name of the Microsoft account the code was issued to; null when Microsoft sent none. */
  microsoftSignInName: string | null
}

/** Look up which Microsoft account a pairing code belongs to. Does not use the code up. */
export default async function postLinkCodePreview(code: string): Promise<LinkCodePreview> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error(kErrorCodeAuthError)

  const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/identity-bridge/link-codes/preview`
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json; charset=UTF-8' },
    body: JSON.stringify({ code }),
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    const refusal = parseOutlookLinkRefusal(response.status, data)
    if (refusal) throw refusal
    const error = new ApiError(response.status, 'The link code could not be checked.')
    Sentry.captureException(error, { extra: getResponseDebugContext(response, apiUrl) })
    throw error
  }

  const parsed = parseError ? null : LinkCodePreviewSchema.safeParse(data)
  if (!parsed?.success) {
    const error = new ApiResponsePayloadError(response.status, 'Invalid link code preview response', parsed?.error.issues)
    Sentry.captureException(error, { extra: getResponseDebugContext(response, apiUrl) })
    throw error
  }
  return { microsoftSignInName: parsed.data.microsoft_sign_in_name }
}
